import { useEffect, useRef, useState } from "react";
import * as E from "@numi/engine";
import type { Question } from "@numi/engine";
import { api } from "../api.ts";
import { set, go } from "../store.ts";
import { Html, Icon, Numi } from "../ui.tsx";
import { SFX } from "../sound.ts";
import { tapCount } from "./Question.tsx";

type Prices = { market: "IN" | "AE"; trialDays: number; pricing: Record<"IN" | "AE", { currency: string; symbol: string; locale: string; plans: Record<"m1" | "m3", { amount: number; list?: number; months: number }> }> };
const money = (p: Prices, m: "IN" | "AE", paise: number) => p.pricing[m].symbol + (paise / 100).toLocaleString(p.pricing[m].locale, { maximumFractionDigits: 0 });

export const startSignup = () => { SFX.tap(); set({ authMode: "signup" }); go("auth"); };
export const startLogin = () => { SFX.tap(); set({ authMode: "login" }); go("auth"); };

/** Lesson preview: shows the first step of a topic before the child attempts a question. */
function LessonCard({ topic, onReady }: { topic: ReturnType<typeof E.topic>; onReady: () => void }) {
  if (!topic) return null;
  const step = topic.lesson[0];
  if (!step) { onReady(); return null; }
  return (
    <div className="lp-lesson">
      <div className="lp-lesson-numi">
        <Numi size={56} mood="" />
        <p className="lp-lesson-say">{E.stepText(step.say)}</p>
      </div>
      <Html className="vizbox sm lp-lesson-viz" html={step.viz()} />
      <button className="btn yellow" onClick={onReady}>Try a question →</button>
    </div>
  );
}

/** Hands-on build question for the landing demo — taps/drags on +/-, blocks, frames etc. actually
 *  mutate the question's state and re-render, same mechanics as the real in-app Build component
 *  (Question.tsx), but with local re-render instead of the global store's touch(). */
function TryBuild({ q, onCheck }: { q: Question; onCheck: (value: string | number) => void }) {
  const [, bump] = useState(0); const render = () => bump((n) => n + 1);
  const ref = useRef<HTMLDivElement>(null); const drag = useRef(false);
  const s = q.st as any;
  const onClick = (e: React.MouseEvent) => {
    const el = e.target as Element;
    const bead = el.closest(".bead") as HTMLElement | null;
    if (bead && q.build === "abacus") { E.abacusTap(s.d, +bead.dataset.r!, bead.dataset.b!); SFX.pop((E.buildValue(q) as number) % 20); render(); return; }
    const cell = el.closest(".cell");
    if (cell && (q.build === "frame" || q.build === "shade")) { const i = +(cell as HTMLElement).dataset.i!; const k = s.on.indexOf(i); k >= 0 ? s.on.splice(k, 1) : s.on.push(i); SFX.pop(s.on.length); render(); return; }
    const bb = el.closest("[data-b]") as HTMLElement | null;
    if (bb) { const k = bb.dataset.b!, d = +bb.dataset.d!; s[k] = Math.max(0, Math.min(k === "o" ? 19 : 9, s[k] + d)); SFX.pop(s[k]); render(); return; }
    const pl = el.closest(".plate") as HTMLElement | null;
    if (pl) { if (!s.pile) { SFX.no(); return; } const i = +pl.dataset.p!; s.plates[i]++; s.pile--; s.hist.push(i); SFX.pop(s.plates[i]); render(); return; }
    if (el.closest("[data-undo]")) { const i = s.hist.pop(); if (i != null) { s.plates[i]--; s.pile++; SFX.tap(); render(); } return; }
    const cb = el.closest("[data-c]") as HTMLElement | null;
    if (cb) { const d = +cb.dataset.d!; if (cb.dataset.c === "h") s.h = ((s.h - 1 + d + 12) % 12) + 1; else { const mm = s.m + 5 * d; if (mm >= 60) { s.m = 0; s.h = (s.h % 12) + 1; } else if (mm < 0) { s.m = 55; s.h = ((s.h - 2 + 12) % 12) + 1; } else s.m = mm; } SFX.tap(); render(); }
  };
  const move = (e: React.PointerEvent) => {
    if (!drag.current || q.build !== "clock") return;
    const svg = ref.current?.querySelector("#clk svg"); if (!svg) return;
    const r = svg.getBoundingClientRect(); const x = ((e.clientX - r.left) / r.width) * 160 - 80, y = ((e.clientY - r.top) / r.height) * 160 - 80;
    if (x * x + y * y < 64) return; let a = (Math.atan2(x, -y) * 180) / Math.PI; if (a < 0) a += 360;
    const mm = (Math.round(a / 30) * 5) % 60; if (mm === s.m) return;
    if (s.m >= 45 && mm <= 10) s.h = (s.h % 12) + 1; else if (s.m <= 10 && mm >= 45) s.h = ((s.h - 2 + 12) % 12) + 1;
    s.m = mm; SFX.tap();
    const tmp = document.createElement("div"); tmp.innerHTML = E.V.clock(s.h, s.m); svg.innerHTML = (tmp.firstChild as Element).innerHTML;
    const bs = ref.current!.querySelectorAll(".bin b"); if (bs.length === 2) { bs[0].textContent = String(s.h); bs[1].textContent = String(s.m).padStart(2, "0"); }
  };
  const canCheck = q.build === "frame" || q.build === "shade" ? s.on.length > 0
    : q.build === "blocks" ? !!(s.h || s.t || s.o)
    : q.build === "share" ? s.pile === 0
    : true;
  return (
    <>
      <div ref={ref} className="lp-build-wrap" onPointerDown={(e) => { if ((e.target as Element).closest("#clk")) { drag.current = true; (e.target as Element).setPointerCapture?.(e.pointerId); move(e); } }}
        onPointerMove={move} onPointerUp={() => { if (drag.current) { drag.current = false; render(); } }} onPointerCancel={() => { drag.current = false; }}>
        <Html className="vizbox lp-build" html={E.buildHTML(q)} onClick={onClick} />
      </div>
      <button className="btn yellow" disabled={!canCheck} onClick={() => onCheck(E.buildValue(q) as string | number)}><Icon name="tick" /> Check</button>
    </>
  );
}

/** One quick question a visitor can try — lesson preview first, then grade picker after first attempt. */
function TryIt({ market }: { market: "IN" | "AE" }) {
  const [grade, setGrade] = useState(2);
  const [showGrades, setShowGrades] = useState(false);
  const [topic, setTopic] = useState<ReturnType<typeof E.topic> | null>(null);
  const [showLesson, setShowLesson] = useState(true);
  const [q, setQ] = useState<Question | null>(null); const [opts, setOpts] = useState<string[]>([]);
  const [n, setN] = useState(0); const [right, setRight] = useState(0);
  const [picked, setPicked] = useState<string | null>(null); const [wrong, setWrong] = useState<(string | number)[]>([]);
  const [numiMood, setNumiMood] = useState<string>("");
  const [numiBump, setNumiBump] = useState(0);

  const newQ = (g: number, t?: ReturnType<typeof E.topic>) => {
    const savedBoard = (E as any).board?.()?.id;
    E.setBoard(market === "AE" ? "uae" : "cbse");
    const picked_topic = t ?? E.pick(E.topicsFor(g));
    if (!t) setTopic(picked_topic);
    const nq = E.nextQuestion(picked_topic.id, 1, { buildChance: 40 });
    if (savedBoard) E.setBoard(savedBoard);
    setQ(nq); setOpts(E.bubbleOptions(nq)); setPicked(null); setWrong([]);
    setNumiMood(""); setNumiBump(0);
  };

  const pickTopic = (g: number) => {
    const savedBoard = (E as any).board?.()?.id;
    E.setBoard(market === "AE" ? "uae" : "cbse");
    const t = E.pick(E.topicsFor(g));
    if (savedBoard) E.setBoard(savedBoard);
    setTopic(t); setShowLesson(true); setN(0); setRight(0); setShowGrades(false);
    setQ(null); setPicked(null); setWrong([]);
  };

  useEffect(() => { pickTopic(grade); }, [grade, market]);

  const gw = market === "AE" ? "Grade" : "Class";

  if (n >= 3) return (
    <div className="lp-try-done">
      <Numi size={90} mood="fly" />
      <h3>{right === 3 ? "3 out of 3. Well done." : `${right} out of 3. Good effort.`}</h3>
      <p>That was just a taste. Inside Numi there are picture lessons, hands-on building and the Learning Abacus.</p>
      <button className="btn yellow big" onClick={startSignup}>Start free trial</button>
    </div>
  );

  // Show lesson preview before the first question
  if (showLesson && topic) return (
    <div className="lp-try">
      <LessonCard topic={topic} onReady={() => { setShowLesson(false); newQ(grade, topic); }} />
    </div>
  );

  if (!q) return null;

  const tap = (o: string | number) => {
    if (picked) return;
    setShowGrades(true);
    if (E.isCorrect(q, o)) {
      SFX.ok();
      setPicked(String(o));
      setNumiMood("wow"); setNumiBump(b => b + 1);
      if (!wrong.length) setRight((r) => r + 1);
    } else {
      SFX.no();
      setWrong((w) => [...w, o]);
      setNumiMood("think"); setNumiBump(b => b + 1);
    }
  };

  // Show specific misconception feedback if engine knows what went wrong
  const diagnoseMsg = wrong.length ? E.diagnose(q, wrong[wrong.length - 1]) : null;
  const warnText = diagnoseMsg || q.hints[0] || "Have another look and try again.";

  return (
    <div className="lp-try">
      {showGrades && (
        <div className="lp-grades" role="group" aria-label={gw}>
          {[1, 2, 3, 4, 5].map((g) => (
            <button key={g} className={g === grade ? "on" : ""} aria-pressed={g === grade}
              onClick={() => { setGrade(g); }}>{gw} {g}</button>
          ))}
        </div>
      )}
      <div className="lp-try-q-row">
        <Numi size={44} mood={numiMood} bump={numiBump} />
        <Html as="p" className="qtext" html={q.text} />
      </div>
      {q.viz && <Html key={n} className="vizbox sm" html={q.viz} onClick={tapCount} />}
      {q.type === "build"
        ? !picked && <TryBuild key={"b" + n} q={q} onCheck={tap} />
        : <div className="choices">{opts.map((o) => (
            <button key={n + o} className={`choice ${picked === o ? "ok" : ""} ${wrong.includes(o) ? "no" : ""}`}
              disabled={wrong.includes(o)} onClick={() => tap(o)}><Html as="span" html={E.bubbleLabel(o, q)} /></button>
          ))}</div>
      }
      {picked
        ? <div className="lp-fb good"><Numi size={32} mood="fly" /><b>Yes!</b> <Html as="span" html={q.explain} /> <button className="btn yellow" onClick={() => { setN(n + 1); newQ(grade); }}>{n < 2 ? "Next question" : "See my score"}</button></div>
        : wrong.length ? <div className="lp-fb warn"><b>So close!</b> <Html as="span" html={warnText} /> Let's look again together — you've got this.</div> : null}
    </div>
  );
}

/** Vedic Maths teaser: a real trick, taught and tried right here, cycling through a showcase set. */
const VEDIC_SHOWCASE = ["v-add9", "v-double", "v-11", "v-near", "v-x5", "v-sq5"];
function VedicTeaser() {
  const [idx, setIdx] = useState(0);
  const [q, setQ] = useState<Question | null>(null); const [opts, setOpts] = useState<string[]>([]);
  const [picked, setPicked] = useState<string | null>(null); const [wrong, setWrong] = useState<(string | number)[]>([]);
  const [numiMood, setNumiMood] = useState(""); const [numiBump, setNumiBump] = useState(0);

  const trick = E.topic(VEDIC_SHOWCASE[idx]);

  useEffect(() => {
    const t = E.topic(VEDIC_SHOWCASE[idx]); if (!t) return;
    const nq = E.nextQuestion(t.id, 1);
    setQ(nq); setOpts(E.bubbleOptions(nq)); setPicked(null); setWrong([]); setNumiMood(""); setNumiBump(0);
  }, [idx]);

  if (!trick) return null;
  const step = trick.lesson[0];

  const tap = (o: string | number) => {
    if (picked || !q) return;
    if (E.isCorrect(q, o)) { SFX.ok(); setPicked(String(o)); setNumiMood("wow"); setNumiBump((b) => b + 1); }
    else { SFX.no(); setWrong((w) => [...w, o]); setNumiMood("think"); setNumiBump((b) => b + 1); }
  };

  return (
    <div className="lp-vedic-demo">
      <div className="lp-vedic-trick">
        <span className="lp-vedic-sutra">{trick.sutra}</span>
        <h3>{E.topicTitle(trick)}</h3>
        <p className="lp-vedic-say">{E.stepText(step.say)}</p>
        {step.viz && <Html className="vizbox sm" html={step.viz()} />}
      </div>
      {q && (
        <div className="lp-vedic-try">
          <div className="lp-try-q-row">
            <Numi size={40} mood={numiMood} bump={numiBump} />
            <Html as="p" className="qtext" html={q.text} />
          </div>
          {q.viz && <Html className="vizbox sm" html={q.viz} />}
          <div className="choices">{opts.map((o) => (
            <button key={o} className={`choice ${picked === o ? "ok" : ""} ${wrong.includes(o) ? "no" : ""}`}
              disabled={wrong.includes(o)} onClick={() => tap(o)}><Html as="span" html={E.bubbleLabel(o, q)} /></button>
          ))}</div>
          {picked && <div className="lp-fb good"><Numi size={28} mood="fly" /><b>Yes! That's the trick.</b> <Html as="span" html={q.explain} /></div>}
        </div>
      )}
      <button className="btn ghost" onClick={() => setIdx((i) => (i + 1) % VEDIC_SHOWCASE.length)}>Try another trick →</button>
    </div>
  );
}

// Numi's learning philosophy, not a feature catalogue — 4 points max, each a short line.
const DIFF: [string, string, string][] = [
  ["hand",  "Step-by-step guidance",  "Helps your child work through the problem instead of just revealing the answer."],
  ["eye",   "Learn by seeing",        "Blocks, ten-frames, number lines and the abacus make abstract maths easy to understand."],
  ["again", "Learn from mistakes",    "Numi notices where your child is struggling and adapts the next question."],
  ["flag",  "Practise at their pace", "No timers, no pressure — your child learns independently, without feeling rushed."],
];

const FAQ: [string, string][] = [
  ["Will Numi just give my child the answer?", "No. Numi teaches the steps first and lets your child try before showing an answer — it's a tutor, not an answer key."],
  ["Does my child need me beside them?", "No. Numi guides your child with simple instructions and hints at every step. You can check the Grown-ups corner after each session to see what they did."],
  ["What curriculum does Numi follow?", "CBSE (NCERT), ICSE and the UAE syllabus, across Classes and Grades 1 to 5. You pick the board when you add your child."],
  ["What happens after the 7-day free trial?", "Nothing automatic. When the trial ends, you choose a plan to continue. No card is charged unless you pick one."],
  ["Can more than one child use Numi?", "Yes. One plan covers all the children you add. Each child has their own progress and their own pace."],
];

export function Landing() {
  const [p, setP] = useState<Prices | null>(null); const [m, setM] = useState<"IN" | "AE">("IN");
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  useEffect(() => { api("/billing/prices").then((r: Prices) => { setP(r); setM(r.market); }).catch(() => {}); }, []);
  const days = p?.trialDays ?? 7;
  const pl = p?.pricing[m].plans;

  return (
    <main className="lp">

      {/* ── 1. Hero — understood within 5 seconds ── */}
      <section className="lp-hero">
        <div className="lp-hero-t">
          <p className="lp-eyebrow">AI MATHS TUTOR · CLASSES 1–5</p>
          <h1>Maths finally<br />makes sense.</h1>
          <p className="lp-lead">Meet Numi — your child's personal AI Maths Tutor that teaches step by step, helps them learn from mistakes, and builds real understanding, not just right answers.</p>
          <button className="btn yellow lp-hero-cta" onClick={startSignup}>Let your child try Numi free →</button>
          <p className="lp-reassure">{days} days free · No card required</p>
        </div>
        <div className="lp-hero-art" aria-label="Numi, your child's maths buddy">
          <Numi size={220} mood="fly" />
        </div>
      </section>

      {/* Curriculum + language proof row, directly under the hero promise */}
      <div className="lp-proof" aria-label="Supported curricula and languages">
        <span>CBSE</span><span aria-hidden="true">·</span>
        <span>ICSE</span><span aria-hidden="true">·</span>
        <span>UAE curriculum</span>
        <span className="lp-proof-sep" aria-hidden="true">|</span>
        <span>English</span><span aria-hidden="true">·</span>
        <span>Hindi</span><span aria-hidden="true">·</span>
        <span>Arabic</span>
      </div>

      {/* ── 2. Let them experience Numi — the strongest conversion driver ── */}
      <section id="try" className="card lp-sec">
        <h2>Don't just take our word for it.<br />Let your child try one.</h2>
        <p className="note">No sign-up needed.</p>
        <TryIt market={m} />
      </section>

      {/* ── 3. Differentiation — the learning philosophy, not a feature catalogue ── */}
      <section className="lp-sec">
        <h2>Numi doesn't just give answers.<br />Numi teaches.</h2>
        <div className="lp-diff">
          {DIFF.map(([ic, h, body]) => (
            <div key={h} className="lp-diffcard">
              <span className="lp-ic"><Icon name={ic} /></span>
              <h3>{h}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 3.5 Vedic Maths — speed and number sense, not just answers ── */}
      <section id="vedic" className="card lp-sec lp-vedicsec">
        <h2>Vedic Maths: the mental-maths tricks Numi teaches.</h2>
        <p className="note">Real tricks from Vedic Maths, taught step by step and practised right away — from Class 1 (fast addition) all the way to Class 5 (criss-cross multiplication). Numi explains why each trick works, not just the shortcut.</p>
        <VedicTeaser />
      </section>

      {/* ── 4. Parent value — the strongest reason to subscribe ── */}
      <section className="lp-sec lp-pval">
        <h2>You don't have to sit beside them.</h2>
        <p className="note">Numi helps your child practise independently while giving you a clear view of what they learned, where they struggled and what comes next.</p>
        <div className="lp-progress" role="img" aria-label="Example of today's learning summary a parent sees">
          <p className="lp-progress-h">TODAY'S LEARNING</p>
          <div className="lp-progress-row"><Icon name="tick" /><div><b>Multiplication</b><span>Practising</span></div></div>
          <div className="lp-progress-row ok"><Icon name="tick" /><div><b>Equal Groups</b><span>Understood</span></div></div>
          <div className="lp-progress-row need"><Icon name="next" /><div><b>Word Problems</b><span>Needs more practice</span></div></div>
          <p className="lp-progress-next">Recommended next step: 10 minutes of guided practice.</p>
        </div>
      </section>

      {/* ── 5. Trust — compact, only claims the product actually supports ── */}
      <section className="lp-sec lp-trustsec">
        <h2>Built for the way children learn maths.</h2>
        <div className="lp-trust" role="list">
          <span role="listitem"><Icon name="tick" />No ads</span>
          <span role="listitem"><Icon name="tick" />Parent controlled</span>
          <span role="listitem"><Icon name="tick" />Maths focused</span>
        </div>
        <p className="note lp-privacy">Your child's activity stays in your account — nothing public, nothing shared outside it.</p>
      </section>

      {/* ── 6. Pricing ── */}
      {p && pl && (
        <section className="lp-sec" id="pricing">
          <h2>Simple pricing.<br />No surprises.</h2>
          <p className="note">Try Numi free for {days} days. No card needed. Cancel anytime.</p>
          <div className="seg" role="group" aria-label="Currency">
            <button className={m === "IN" ? "on" : ""} onClick={() => setM("IN")}>India (₹)</button>
            <button className={m === "AE" ? "on" : ""} onClick={() => setM("AE")}>UAE (AED)</button>
          </div>
          <div className="pcards">
            <div className="pcard">
              <span className="pname">1 month</span>
              <span className="pprice">{money(p, m, pl.m1.amount)}</span>
              <span className="psub">30 days</span>
            </div>
            <div className="pcard best">
              {pl.m3.list && <span className="pbadge">Save {money(p, m, pl.m3.list - pl.m3.amount)}</span>}
              <span className="pname">3 months</span>
              <span className="pprice">{pl.m3.list ? <s>{money(p, m, pl.m3.list)}</s> : null} {money(p, m, pl.m3.amount)}</span>
              <span className="psub">Best for building a learning habit</span>
            </div>
          </div>
          <button className="btn yellow big" onClick={startSignup}>Start {days}-day free trial →</button>
          <p className="note lp-pcompact">Personal AI Maths Tutor · Guided Practice · Parent Progress</p>
        </section>
      )}

      {/* ── 7. FAQ ── */}
      <section className="lp-sec" id="faq">
        <h2>Common questions</h2>
        <div className="faq-list">
          {FAQ.map(([q, a], i) => (
            <div key={i} className="faq-item">
              <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                {q} <span className="faq-arr">{openFaq === i ? "▲" : "▼"}</span>
              </button>
              {openFaq === i && <p className="faq-a">{a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ── 8. Final CTA — emotional but restrained ── */}
      <section className="card lp-end">
        <Numi size={90} mood="fly" />
        <div>
          <h2>Ready to make maths click?</h2>
          <p>Give your child a smarter way to learn, practise and build confidence in maths.</p>
        </div>
        <div className="lp-end-cta">
          <button className="btn yellow big" onClick={startSignup}>Let your child try Numi free →</button>
          <p className="lp-reassure">{days} days free · No card required</p>
        </div>
      </section>

    </main>
  );
}
